import React, { useEffect, useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Renders the official PayPal Smart Buttons for a car reservation deposit.
 * Automatically fetches the PayPal client id + mode from the backend
 * (`/api/paypal/config`) so the operator only needs to set the env keys in
 * `backend/.env` (PAYPAL_CLIENT_ID, PAYPAL_SECRET, PAYPAL_MODE).
 *
 * Props:
 *  - carId (string, required)
 *  - customer ({name, surname, email, phone, notes})
 *  - onSuccess(reservationId) — called after a completed capture
 *  - onError(err)
 */
export const PayPalCheckout = ({ carId, customer, onSuccess, onError }) => {
  const [cfg, setCfg] = useState({ enabled: false, client_id: null, mode: "sandbox" });
  const [loadingCfg, setLoadingCfg] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/paypal/config")
      .then((r) => { if (!cancelled) setCfg(r.data || {}); })
      .catch(() => { if (!cancelled) setCfg({ enabled: false, client_id: null }); })
      .finally(() => { if (!cancelled) setLoadingCfg(false); });
    return () => { cancelled = true; };
  }, []);

  const options = useMemo(
    () => ({ clientId: cfg.client_id, currency: "EUR", intent: "capture" }),
    [cfg.client_id]
  );

  if (loadingCfg) {
    return (
      <div className="p-4 text-xs text-white/50 text-center" data-testid="paypal-loading">
        Caricamento PayPal…
      </div>
    );
  }

  if (!cfg.enabled || !cfg.client_id) {
    return (
      <div className="border border-orange-500/30 bg-orange-500/5 p-4 text-xs text-orange-300 leading-relaxed" data-testid="paypal-not-configured">
        PayPal non ancora configurato. Aggiungi <code>PAYPAL_CLIENT_ID</code> e <code>PAYPAL_SECRET</code> in <code>backend/.env</code> per attivarlo.
      </div>
    );
  }

  const createOrder = async () => {
    try {
      const r = await api.post("/paypal/create-order", { car_id: carId, ...customer });
      return r.data.order_id;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore creazione ordine PayPal";
      toast.error(msg);
      onError && onError(err);
      throw err;
    }
  };

  const onApprove = async (data) => {
    try {
      const r = await api.post(`/paypal/capture-order/${data.orderID}`);
      if (r.data.success) {
        toast.success("Pagamento ricevuto");
        onSuccess && onSuccess(r.data.reservation_id);
      } else {
        toast.error("Pagamento non completato");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore conferma pagamento");
      onError && onError(err);
    }
  };

  return (
    <div data-testid="paypal-buttons-wrapper">
      {cfg.mode === "sandbox" && (
        <div className="text-[10px] uppercase tracking-widest text-orange-300/70 text-center mb-2" data-testid="paypal-sandbox-badge">
          Modalità Sandbox — pagamento di test
        </div>
      )}
      <PayPalScriptProvider options={options}>
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={(err) => {
            toast.error("Errore PayPal, riprova");
            onError && onError(err);
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckout;
